import { z } from '../../zod';
import { PaginatedListSchema } from '../../shared/paginatedList';
import {
  ThemeOverridesResponseValidationSchema,
  ThemeOverridesToolResponseValidationSchema,
} from '../themeOverrides';
import { ThemeScopeValidationEnum } from './list';

const themeFields = {
  id: z.string().meta({ description: 'Unique identifier for the theme.' }),
  name: z.string().meta({ description: 'Theme name, unique within your account.' }),
  scope: ThemeScopeValidationEnum,
  deprecated: z.boolean().meta({
    description:
      'A retired built-in theme. It is only listed while one of your pages uses it, and it cannot be applied to another page.',
  }),
  createdAt: z.string().nullable().meta({ description: 'ISO-8601 time the theme was created.' }),
  updatedAt: z
    .string()
    .nullable()
    .meta({ description: 'ISO-8601 time the theme was last changed.' }),
};

const VARIABLES_DESCRIPTION =
  "The theme's design and labels, in the same shape as a page's `themeOverrides`. Order bump styling, which only the dashboard can change, is kept but not returned. `null` when the theme sets none.";

// Field-level meta on variables would inline the whole ThemeOverrides schema here instead of a $ref.
export const ThemeResponseValidationSchema = z
  .object({
    ...themeFields,
    variables: ThemeOverridesResponseValidationSchema.nullable(),
  })
  .meta({
    id: 'Theme',
    description:
      "A reusable page design. Pages use one theme and can override parts of it. `variables` holds the theme's design and labels in the same shape as a page's `themeOverrides`: order bump styling, which only the dashboard can change, is kept but not returned, and it is `null` when the theme sets none.",
  });

export const ThemeToolResponseValidationSchema = z.object({
  ...themeFields,
  variables: ThemeOverridesToolResponseValidationSchema.meta({
    description: VARIABLES_DESCRIPTION,
  }),
});

export type ThemeResponse = z.infer<typeof ThemeResponseValidationSchema>;

export const GetThemeResponseValidationSchema = z.object({ data: ThemeResponseValidationSchema });
export const GetThemesResponseValidationSchema = PaginatedListSchema(ThemeResponseValidationSchema);
export const GetThemeToolResponseValidationSchema = z.object({
  data: ThemeToolResponseValidationSchema,
});
export const GetThemesToolResponseValidationSchema = PaginatedListSchema(
  ThemeToolResponseValidationSchema,
);

export const CreateThemeResponseValidationSchema = z.object({
  data: ThemeResponseValidationSchema,
});
export const UpdateThemeResponseValidationSchema = z.object({
  data: ThemeResponseValidationSchema,
});
export const CreateThemeToolResponseValidationSchema = z.object({
  data: ThemeToolResponseValidationSchema,
});
export const UpdateThemeToolResponseValidationSchema = z.object({
  data: ThemeToolResponseValidationSchema,
});
export const DeleteThemeResponseValidationSchema = z.object({
  data: ThemeResponseValidationSchema,
});
export const DeleteThemeToolResponseValidationSchema = z.object({
  data: ThemeToolResponseValidationSchema,
});
